import { h, Fragment } from "preact";
import { RoutableProps } from "preact-router";
import { useEffect, useMemo, useState } from "preact/hooks";
import { resolvePath } from "../../../api";
import { generate, getLabelIds, labelPreviewUrl, labelsMetadata } from "../../../api/label-creator";
import { createLabel, deleteLabel, editLabel, getLabel, getTemplateLabels, Label } from "../../../api/product-api";
import { route } from "../../../App";
import { Snackbar, SnackbarShowCommand } from "../../../components/Snackbar";
import { TemplatePrint, parseTapeType } from "../../../components/TemplatePrint";
import { useCommand } from "../../../hooks";
import { generateExampleFieldsForLabel, parseToDateAndFormatIfExists } from "../../../utils";
import { predefinedMetadataKeys, TemplateMetadataDisplay } from "../../../components/TemplateMetadataDisplay";
import "./styles.scss";
import { LabelMetadataEditorHelp } from "../LabelMetadataEditorHelp";
import { LabelMetadataFieldEdit } from "../LabelMetadataFieldEdit";
import { LabelSVGInteractive } from "../LabelSVGInteractive";
import { CompactTableItemSelector } from "../../../components/CompactTableItemSelector";
import { countries } from "../../../data/countries";

const predefinedFields = [
    {
        id: "field-date",
        type: "date",
        description: "Data produkcji",
    },
    {
        id: "field-serial",
        type: "",
        description: "Numer seryjny",
    },
    {
        id: "field-gtin",
        type: "text",
        description: "Numer GTIN 14",
    },
    {
        id: "field-gtin13",
        type: "text",
        description: "Numer GTIN 13",
    },
    {
        id: "field-model",
        type: "text",
        description: "Model",
    },
    {
        id: "field-version",
        type: "text",
        description: "Wersja",
    },
];

const predefinedHelperEntries = [
    {
        id: "gs1-production-date",
        defaultValue: "{field-date}",
        description: "GS1 - Data produkcji",
    },
    {
        id: "gs1-serial-number",
        defaultValue: "{field-serial}",
        description: "GS1 - Numer seryjny",
    },
    {
        id: "gs1-gtin",
        defaultValue: "{field-gtin}",
        description: "GS1 - Numer GTIN 14",
    },
    {
        id: "gs1-gtin13",
        defaultValue: "{field-gtin13}",
        description: "GS1 - Numer GTIN 13",
    },
    {
        id: "gs1-lot",
        defaultValue: "",
        description: "GS1 - LOT",
    },
    {
        id: "gs1-lot-date",
        defaultValue: "{field-date}",
        description: "GS1 - LOT - Data",
    },
];

function FieldEdit(props: {
    metadata: { readonly [key: string]: string };
    onChange: (property: string, newValue: string | null) => void;
}) {
    const [newFieldName, setNewFieldName] = useState("");
    const [newPredefinedFieldName, setNewPredefinedFieldName] = useState("");
    const availablePredefinedFields = predefinedFields.filter(v => !Object.keys(props.metadata).includes(v.id));

    useMemo(() => {
        setNewPredefinedFieldName(availablePredefinedFields[0]?.id ?? "");
    }, [availablePredefinedFields.length]);

    return (
        <>
            <div>
                <select
                    value={newPredefinedFieldName}
                    disabled={!availablePredefinedFields.length}
                    style={{ pointerEvents: availablePredefinedFields.length ? "" : "none" }}
                    onChange={e => setNewPredefinedFieldName(e.currentTarget.value)}
                >
                    {availablePredefinedFields.map(v => (
                        <option value={v.id}>{v.description}</option>
                    ))}
                </select>
                <button
                    style="margin-left: 10px;"
                    onClick={() => {
                        const predefinedField = predefinedFields.find(v => v.id === newPredefinedFieldName);

                        if (predefinedField) {
                            props.onChange(newPredefinedFieldName, predefinedField.type);
                            props.onChange("description-" + newPredefinedFieldName, predefinedField.description);
                        }
                    }}
                >
                    Dodaj predefiniowane pole
                </button>
            </div>
            <div style="margin-top: 10px">
                <input
                    type="text"
                    placeholder="Nazwa pola..."
                    value={newFieldName}
                    onChange={e => setNewFieldName(e.currentTarget.value)}
                />
                <button
                    class="secondary"
                    style="margin-left: 10px;"
                    onClick={() => props.onChange("field-" + newFieldName, "")}
                >
                    Dodaj własne pole
                </button>
            </div>
            <div style="display: grid; grid-template-columns: auto 1fr; row-gap: 10px; column-gap: 5px; margin-top: 10px;">
                {Object.keys(props.metadata)
                    .filter(v => v.startsWith("field-"))
                    .map(v => (
                        <>
                            <span style="display: flex; align-items: center; justify-content: flex-end">
                                {props.metadata["description-" + v]
                                    ? `${props.metadata["description-" + v]} (Pole '${v.substring(6)}')`
                                    : `Pole '${v.substring(6)}'`}
                                :
                            </span>
                            <div style="display: flex; align-items: center">
                                <select
                                    style="margin-left: 5px"
                                    type="text"
                                    value={props.metadata[v] ?? ""}
                                    onChange={e => {
                                        props.onChange(v, e.currentTarget.value);
                                    }}
                                >
                                    {predefinedMetadataKeys["field-"].autofill!.map(v => (
                                        <option value={v}>{v}</option>
                                    ))}
                                </select>
                                <input
                                    style="margin-left: 5px"
                                    placeholder="Opis..."
                                    value={props.metadata["description-" + v] ?? ""}
                                    onChange={e => {
                                        props.onChange("description-" + v, e.currentTarget.value);
                                    }}
                                />
                                <button
                                    class="icon flat error"
                                    style="margin-left: 5px; width: 24px; height: 24px; font-size: 18px"
                                    onClick={() => {
                                        props.onChange(v, null);
                                    }}
                                >
                                    delete
                                </button>
                            </div>
                        </>
                    ))}
            </div>
        </>
    );
}

function HelperEntryEdit(props: {
    metadata: { readonly [key: string]: string };
    onChange: (property: string, newValue: string | null) => void;
}) {
    const [newPredefinedEntryName, setNewPredefinedFieldName] = useState("");
    const availablePredefinedEntries = predefinedHelperEntries.filter(v => !Object.keys(props.metadata).includes(v.id));

    useMemo(() => {
        setNewPredefinedFieldName(availablePredefinedEntries[0]?.id ?? "");
    }, [availablePredefinedEntries.length]);

    return (
        <>
            <div>
                <select
                    value={newPredefinedEntryName}
                    disabled={!availablePredefinedEntries.length}
                    style={{ pointerEvents: availablePredefinedEntries.length ? "" : "none" }}
                    onChange={e => setNewPredefinedFieldName(e.currentTarget.value)}
                >
                    {availablePredefinedEntries.map(v => (
                        <option value={v.id}>{v.description}</option>
                    ))}
                </select>
                <button
                    style="margin-left: 10px;"
                    onClick={() => {
                        const predefinedEntry = predefinedHelperEntries.find(v => v.id === newPredefinedEntryName);

                        if (predefinedEntry) {
                            props.onChange(newPredefinedEntryName, predefinedEntry.defaultValue);
                        }
                    }}
                >
                    Dodaj wpis
                </button>
            </div>
            <div style="display: grid; grid-template-columns: auto 1fr; row-gap: 10px; column-gap: 5px; margin-top: 10px;">
                {Object.keys(props.metadata)
                    .filter(v => !v.startsWith("field-") && !v.startsWith("element-") && !v.startsWith("description-"))
                    .filter(v => predefinedHelperEntries.find(e => e.id === v))
                    .map(v => (
                        <>
                            <span style="display: flex; align-items: center; justify-content: flex-end">
                                {predefinedHelperEntries.find(e => e.id === v)?.description}:
                            </span>
                            <span style="display: flex; align-items: center">
                                <LabelMetadataFieldEdit
                                    metadata={props.metadata}
                                    property={v}
                                    onChange={value => props.onChange(v, value)}
                                />
                                <button
                                    class="icon flat error"
                                    style="margin-left: 5px; width: 24px; height: 24px; font-size: 18px"
                                    onClick={() => props.onChange(v, null)}
                                >
                                    delete
                                </button>
                            </span>
                        </>
                    ))}
            </div>
        </>
    );
}

export function LabelEditPage(props: RoutableProps & { id?: string }) {
    const [label, setLabel] = useState(null as null | Label);
    const [labelSvg, setLabelSvg] = useState("");
    const [templateLabelSvg, setTemplateLabelSvg] = useState("");
    const [templateLabels, setTemplatesLabels] = useState(null as null | Label[]);
    const [templateLabel, setTemplateLabel] = useState(null as null | Label);
    const [labelIds, setLabelIds] = useState([] as string[]);
    const [labelMetadata, setLabelMetadata] = useState({} as { [key: string]: string });
    const [ordersSearch, setOrdersSearch] = useState("");
    const [newFieldName, setNewFieldName] = useState("");
    const [newElementName, setNewElementName] = useState("");
    const [newEntryName, setNewEntryName] = useState("");
    const [previewVisible, setPreviewVisible] = useState(true);
    const [labelElementHighlight, setLabelElementHighlight] = useState("");
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const templateMetadataDisplayClear = useCommand();
    const templateMetadataDisplaySetMetadata = useCommand<[metadata: { [key: string]: string }]>();
    const [labelSvgOpacity, setLabelSvgOpacity] = useState(0);
    const [templateLabelSvgOpacity, setTemplateLabelSvgOpacity] = useState(0);
    const [overrideSafetyMeasures, setOverrideSafetyMeasures] = useState(false);

    useEffect(() => {
        if (props.id) {
            getLabel(+props.id).then(async label => {
                setLabel(label);
                setTemplateLabel(label.parentLabel);

                fetch(labelPreviewUrl(label.path)).then(v => v.text().then(t => setLabelSvg(t)));
            });
        } else {
            setLabel({} as Label);
        }

        getTemplateLabels().then(labels => {
            setTemplatesLabels(labels);
        });
    }, [props.id]);

    useEffect(() => {
        if (!label?.path) {
            setLabelIds([]);
            setLabelMetadata({});
            return;
        }

        getLabelIds(label.path)
            .then(async label => {
                setLabelIds(label);
            })
            .catch(() => {
                showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
                setLabelIds([]);
            });

        labelsMetadata(label.path)
            .then(async label => {
                setLabelMetadata(label);
            })
            .catch(() => {
                showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
                setLabelIds([]);
            });
    }, [label?.path]);

    // useEffect(() => {
    //     if (!label?.path) {
    //         setLabelSvg("");
    //         return;
    //     }

    //     fetch(generate(label.path, {}, label.metadata?.metadata, true, true)).then(v => v.text().then(t => {
    //         setLabelSvg(t);

    //         const length = parseFloat((/viewBox="\d+ \d+ (\d+) \d+"/i.exec(t) ?? ["", ""])[1]);

    //         if (label.metadata?.metadata) {
    //             label.metadata.metadata["label-length"] = (length / 14 + 2).toPrecision(2) + "mm";
    //         }
    //     }));
    // }, [label?.path, label?.metadata?.metadata])

    useEffect(() => {
        if (!templateLabel) {
            setTemplateLabelSvg("");
            setLabelSvg("");
            setLabel({ metadata: { metadata: {} } } as Label);
            return;
        }

        if (label?.parentLabel?.id !== templateLabel.id) {
            setLabel({
                ...label,
                path: templateLabel.path,
                parentLabel: templateLabel,
                type: "normal",
                metadata: {
                    metadata: {
                        ...templateLabel.metadata?.metadata,
                    },
                },
            } as Label);
        }

        fetch(labelPreviewUrl(templateLabel.path)).then(v =>
            v.text().then(t => {
                setTemplateLabelSvg(t);

                const length = parseFloat((/viewBox="\d+ \d+ (\d+) \d+"/i.exec(t) ?? ["", ""])[1]);

                if (templateLabel.metadata?.metadata) {
                    templateLabel.metadata.metadata["label-length"] = (length / 14 + 2).toPrecision(2) + "mm";
                }
            }),
        );

        fetch(generate(templateLabel.path, {}, templateLabel.metadata?.metadata, true, true)).then(v =>
            v.text().then(t => {
                setLabelSvg(t);

                const length = parseFloat((/viewBox="\d+ \d+ (\d+) \d+"/i.exec(t) ?? ["", ""])[1]);

                if (templateLabel.metadata?.metadata) {
                    templateLabel.metadata.metadata["label-length"] = (length / 14 + 2).toPrecision(2) + "mm";
                }
            }),
        );
    }, [templateLabel]);

    useEffect(() => {
        if (templateLabel && templateLabels && templateLabel !== templateLabels.find(v => templateLabel.id === v.id)) {
            setTemplateLabel(templateLabels.find(v => templateLabel.id === v.id)!);
        }
    }, [templateLabel, templateLabels]);

    if (!label || !templateLabels) {
        return (
            <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
                <div>Pobieranie danych...</div>
                <div class="loader"></div>
            </div>
        );
    }

    return (
        <div class="label-edit-page">
            <Snackbar doShow={showSnackbarCommand} />
            <div class="order-items paper" style="margin-top: 10px;">
                <h1>{label.id ? "Naklejka: " + label.name : "Nowa naklejka"}</h1>
                <div>
                    <label>
                        <span style="width: 100px; text-align: right">Nazwa naklejki:</span>{" "}
                        <input
                            style="margin-left: 5px"
                            type="text"
                            value={label.name}
                            onChange={e => {
                                setLabel({
                                    ...label,
                                    name: e.currentTarget.value,
                                });
                            }}
                        />
                    </label>
                </div>
                <div style="margin-top: 10px;">
                    <label>
                        <span style="width: 100px; text-align: right">Rewizja:</span>{" "}
                        <input
                            style="margin-left: 5px"
                            type="text"
                            value={label.revision}
                            onChange={e => {
                                setLabel({
                                    ...label,
                                    revision: e.currentTarget.value,
                                });
                            }}
                        />
                    </label>
                </div>
                <div style="margin-top: 10px;">
                    <label>
                        <span style="width: 100px; text-align: right;">Kraj:</span>{" "}
                        <select
                            style="margin-left: 5px;"
                            value={label.metadata?.metadata?.country ?? ""}
                            onChange={e => {
                                setLabel({
                                    ...label,
                                    metadata: {
                                        ...label.metadata,
                                        metadata: {
                                            ...label.metadata?.metadata,
                                            country: e.currentTarget.value,
                                        },
                                    },
                                });
                            }}
                        >
                            <option value={""}>Standardowe</option>
                            {Object.entries(countries).map(([key, value]) => (
                                <option value={key}>
                                    {value} ({key})
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
                <div style="margin-top: 20px;">
                    <h2>Wybierz szablon naklejki</h2>
                    <CompactTableItemSelector
                        items={templateLabels}
                        selected={templateLabel}
                        properties={[
                            { name: "name", type: "text", title: "Nazwa", search: true },
                            { name: "path", type: "text", title: "Ścieżka", search: true },
                            { name: "revision", type: "text", title: "Rewizja", search: true },
                            { name: "creator.email", type: "text", title: "Rejestrujący", search: true },
                            { name: "createdAt", type: "date", title: "Data rejestracji", search: true },
                        ]}
                        onSelect={v => {
                            setTemplateLabel(v);
                        }}
                    />
                </div>
                <div style="margin-top: 20px;">
                    <h2>Podgląd szablonu</h2>
                    <div style="display: flex; position: relative; height: 320px; align-items: center; flex-wrap: wrap; gap: 10px;">
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                display: "flex",
                                "flex-direction": "column",
                                "justify-content": "center",
                                "align-items": "center",
                                width: "100%",
                                height: "100%",
                                opacity: templateLabel ? 1 - templateLabelSvgOpacity : 0,
                                transition: "opacity 0.5s",
                            }}
                        >
                            <div>Pobieranie danych...</div>
                            <div class="loader"></div>
                        </div>
                        <div>
                            <div
                                style={{
                                    "margin-bottom": "5px",
                                    opacity: templateLabelSvgOpacity,
                                    transition: "opacity 0.5s",
                                }}
                            >
                                Podgląd szablonu
                            </div>
                            <LabelSVGInteractive
                                maxWidth={500}
                                maxHeight={300}
                                svg={templateLabelSvg}
                                highlight={labelElementHighlight}
                                onReload={() => setTemplateLabelSvgOpacity(0)}
                                onLoad={() => setTemplateLabelSvgOpacity(1)}
                            />
                        </div>
                        <div>
                            <div
                                style={{ "margin-bottom": "5px", opacity: labelSvgOpacity, transition: "opacity 0.5s" }}
                            >
                                Przykład wypełnionej naklejki
                            </div>
                            <LabelSVGInteractive
                                maxWidth={500}
                                maxHeight={300}
                                svg={labelSvg}
                                onReload={() => setLabelSvgOpacity(0)}
                                onLoad={() => setLabelSvgOpacity(1)}
                            />
                        </div>
                    </div>
                </div>
                <div style="margin-top: 20px;">
                    <h2>
                        <span
                            class="tooltip tooltip-right"
                            data-tooltip="Pola możliwe do wypełnienia przez użytkownika lub system"
                        >
                            Pola
                        </span>
                    </h2>
                    <FieldEdit
                        metadata={label.metadata?.metadata ?? {}}
                        onChange={(prop, value) => {
                            if (value !== null) {
                                setLabel(prev =>
                                    prev
                                        ? {
                                              ...prev,
                                              metadata: {
                                                  ...prev.metadata,
                                                  metadata: {
                                                      ...prev.metadata?.metadata,
                                                      [prop]: value,
                                                  },
                                              },
                                          }
                                        : prev,
                                );
                            } else {
                                const newLabel = {
                                    ...label,
                                    metadata: {
                                        ...label.metadata,
                                        metadata: {
                                            ...label.metadata?.metadata,
                                        },
                                    },
                                };

                                delete newLabel.metadata.metadata[prop];

                                setLabel(newLabel);
                            }
                        }}
                    />
                </div>
                <div style="margin-top: 20px;">
                    <h2>Wpisy pomocnicze</h2>
                    <HelperEntryEdit
                        metadata={label.metadata?.metadata ?? {}}
                        onChange={(prop, value) => {
                            if (value !== null) {
                                setLabel(prev =>
                                    prev
                                        ? {
                                              ...prev,
                                              metadata: {
                                                  ...prev.metadata,
                                                  metadata: {
                                                      ...prev.metadata?.metadata,
                                                      [prop]: value,
                                                  },
                                              },
                                          }
                                        : prev,
                                );
                            } else {
                                const newLabel = {
                                    ...label,
                                    metadata: {
                                        ...label.metadata,
                                        metadata: {
                                            ...label.metadata?.metadata,
                                        },
                                    },
                                };

                                delete newLabel.metadata.metadata[prop];

                                setLabel(newLabel);
                            }
                        }}
                    />
                </div>
                <div style="margin-top: 20px;">
                    <h2>Elementy</h2>
                    <div style="display: grid; grid-template-columns: auto 1fr; row-gap: 10px; column-gap: 5px;">
                        {Object.keys(label.metadata?.metadata ?? {})
                            .filter(v => v.startsWith("element-"))
                            .map(v => (
                                <>
                                    <span
                                        class={
                                            labelIds.indexOf(v.split("-").slice(2).join("-")) < 0
                                                ? "tooltip tooltip-error"
                                                : ""
                                        }
                                        style={{
                                            backgroundColor:
                                                labelIds.indexOf(v.split("-").slice(2).join("-")) < 0
                                                    ? "var(--palette-error-dark)"
                                                    : "",
                                            padding: "5px 0px",
                                            textAlign: "right",
                                        }}
                                        data-tooltip="Element o tej nazwie nie wsytępuje w pliku SVG"
                                        onMouseEnter={() => setLabelElementHighlight(v.split("-").slice(2).join("-"))}
                                        onMouseLeave={() => setLabelElementHighlight("")}
                                    >
                                        {(label.metadata?.metadata ?? {})["description-" + v] ??
                                            `Element '${v.split("-").slice(2).join("-")}'`}
                                        :
                                    </span>
                                    <div>
                                        <LabelMetadataFieldEdit
                                            metadata={label.metadata?.metadata ?? {}}
                                            property={v}
                                            onChange={value => {
                                                setLabel({
                                                    ...label,
                                                    metadata: {
                                                        ...label.metadata,
                                                        metadata: {
                                                            ...label.metadata?.metadata,
                                                            [v]: value,
                                                        },
                                                    },
                                                });
                                            }}
                                        />
                                    </div>
                                </>
                            ))}
                    </div>
                </div>
                <div style="margin-top: 20px;">
                    <h2>Podgląd wydruku</h2>
                    <div style="margin-bottom: 10px;">
                        <img
                            style="max-width: 800px; max-height: 500px"
                            src={generate(
                                label.path,
                                generateExampleFieldsForLabel({ ...labelMetadata, ...label.metadata?.metadata }),
                                label.metadata?.metadata,
                            )}
                        />
                    </div>
                </div>
                <div class="accordion" style="margin-top: 10px;">
                    <div>
                        <input type="radio" name="advanced-accordion" />
                        <input type="radio" name="advanced-accordion" />
                        <div class="header">Zaawansowane</div>
                        <div class="content" style="flex-direction: column">
                            <div style="margin-top: 20px;">
                                <h2>Dodatkowe wpisy</h2>
                                <input
                                    type="text"
                                    placeholder="Klucz wpisu..."
                                    value={newEntryName}
                                    onChange={e => setNewEntryName(e.currentTarget.value)}
                                />
                                <button
                                    style="margin-left: 10px;"
                                    onClick={() => {
                                        if (!newEntryName) {
                                            return;
                                        }

                                        setLabel({
                                            ...label,
                                            metadata: {
                                                ...label.metadata,
                                                metadata: {
                                                    ...label.metadata?.metadata,
                                                    [newEntryName]: "",
                                                },
                                            },
                                        });
                                    }}
                                >
                                    Dodaj wpis
                                </button>
                            </div>
                            {Object.keys(label.metadata?.metadata ?? {})
                                .filter(
                                    v =>
                                        !v.startsWith("field-") &&
                                        !v.startsWith("element-") &&
                                        !v.startsWith("description-"),
                                )
                                .filter(v => Object.keys(predefinedMetadataKeys).indexOf(v) < 0)
                                .map(v => (
                                    <div style="margin-top: 10px; display: flex; align-items: center;">
                                        <label>
                                            Wpis '{v}':{" "}
                                            <LabelMetadataFieldEdit
                                                metadata={label.metadata?.metadata ?? {}}
                                                property={v}
                                                onChange={value => {
                                                    setLabel({
                                                        ...label,
                                                        metadata: {
                                                            ...label.metadata,
                                                            metadata: {
                                                                ...label.metadata?.metadata,
                                                                [v]: value,
                                                            },
                                                        },
                                                    });
                                                }}
                                            />
                                        </label>
                                        <button
                                            class="icon flat error"
                                            style="margin-left: 5px; width: 24px; height: 24px; font-size: 18px"
                                            onClick={() => {
                                                const newLabel = {
                                                    ...label,
                                                    metadata: {
                                                        ...label.metadata,
                                                        metadata: {
                                                            ...label.metadata?.metadata,
                                                        },
                                                    },
                                                };

                                                delete newLabel.metadata.metadata[v];

                                                setLabel(newLabel);
                                            }}
                                        >
                                            delete
                                        </button>
                                    </div>
                                ))}
                            <div
                                style="margin-top: 10px; padding:10px; margin-left: 10px; margin-right: 10px;"
                                class="paper-2"
                            >
                                <h1>Metadane</h1>
                                <div>
                                    <TemplateMetadataDisplay
                                        template={label.metadata}
                                        loadFromStorage={false}
                                        onChange={metadata => {
                                            setLabel({ ...label, metadata: { ...label.metadata, metadata } });
                                        }}
                                        doClear={templateMetadataDisplayClear}
                                        doSetMetadata={templateMetadataDisplaySetMetadata}
                                    />
                                </div>
                                <div style="margin-top: 10px; display: flex; justify-content: flex-end">
                                    <button
                                        type="button"
                                        class="info"
                                        onClick={async () => {
                                            await navigator.clipboard.writeText(
                                                Object.entries(label.metadata?.metadata ?? {})
                                                    .map(([key, value]) => `${key}=${value}`)
                                                    .join("\n"),
                                            );
                                            showSnackbarCommand.run("Skopiowano", "info", "info", 2000);
                                        }}
                                    >
                                        <span class="material-icons" style="font-size: 17px; margin-right: 5px">
                                            content_copy
                                        </span>
                                        Kopiuj
                                    </button>
                                    <button
                                        type="button"
                                        style="margin-left: 10px;"
                                        class="error"
                                        onClick={() => templateMetadataDisplayClear.run()}
                                    >
                                        Wyczyść
                                    </button>
                                </div>
                                <div
                                    style={{
                                        position: "fixed",
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        zIndex: 100,
                                        display: "flex",
                                        direction: "rtl",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <div
                                        style={{
                                            pointerEvents: "all",
                                            resize: "horizontal",
                                            padding: "10px 10px",
                                            background: "#3f3f3f",
                                            overflow: "hidden",
                                            overflowY: "auto",
                                            maxWidth: "600px",
                                            maxHeight: "350px",
                                            borderTop: "1px solid #5f5f5f",
                                            borderRight: "1px solid #5f5f5f",
                                            boxShadow: "#00000047 0px 0px 10px 2px",
                                        }}
                                    >
                                        <img
                                            style="width: 100%"
                                            src={generate(
                                                label.path,
                                                generateExampleFieldsForLabel({
                                                    ...labelMetadata,
                                                    ...label.metadata?.metadata,
                                                }),
                                                label.metadata?.metadata,
                                            )}
                                        />
                                    </div>
                                </div>
                                <h2>Podgląd taśmy</h2>
                                <div>
                                    <img
                                        style="max-width: 200px"
                                        src={`${resolvePath("images/tapes/")}${parseTapeType(label.metadata?.metadata["tape-width"], label.metadata?.metadata["tape-type"] ?? "")}`}
                                    />
                                </div>
                                <LabelMetadataEditorHelp />
                            </div>
                            <div style="margin-top: 10px;">
                                <h2>Drukowanie</h2>
                            </div>
                            <div style="padding: 0 10px;">
                                <TemplatePrint
                                    template={label.path}
                                    fields={{}}
                                    overrideFields={{ ...labelMetadata, ...label.metadata?.metadata }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {label.id ? (
                    <div class="mt-2 flex flex-row">
                        <span
                            class={
                                (label.productTypeRevisionLabels ?? []).length > 0 && !overrideSafetyMeasures
                                    ? "tooltip"
                                    : ""
                            }
                            data-tooltip="Nie można edydotwać naklejki, która została przypisana do rewizji produktu"
                        >
                            <button
                                type="button"
                                class={
                                    (label.productTypeRevisionLabels ?? []).length > 0 && !overrideSafetyMeasures
                                        ? "disabled"
                                        : ""
                                }
                                onClick={async () => {
                                    try {
                                        await editLabel(label);
                                    } catch (ex) {
                                        console.error(ex);
                                        showSnackbarCommand.run(
                                            "Wystąpił błąd podczas edytowania naklejki",
                                            "error",
                                            "error",
                                        );
                                        return;
                                    }

                                    route("/label", true);
                                }}
                            >
                                Zastosuj
                            </button>
                        </span>
                        {(label.productTypeRevisionLabels ?? []).length > 0 && (
                            <label style="margin-left: 15px;">
                                Jestem świadomy, że zmiana wizualna naklejki, która została już wydrukowana przez
                                produkcje może stworzyć problemy{" "}
                                <input
                                    type="checkbox"
                                    style="margin-left: 5px"
                                    checked={overrideSafetyMeasures}
                                    onChange={e => setOverrideSafetyMeasures(e.currentTarget.checked)}
                                />
                            </label>
                        )}
                        <span class="grow" />
                        <span
                            class={(label.productTypeRevisionLabels ?? []).length > 0 ? "tooltip" : ""}
                            data-tooltip="Nie można usunąć naklejki, która została przypisana do rewizji produktu"
                        >
                            <button
                                type="button"
                                class={
                                    "error" + ((label.productTypeRevisionLabels ?? []).length > 0 ? " disabled" : "")
                                }
                                onClick={async () => {
                                    try {
                                        await deleteLabel(label.id);
                                    } catch (ex) {
                                        console.error(ex);
                                        showSnackbarCommand.run(
                                            "Wystąpił błąd podczas usuwania naklejki",
                                            "error",
                                            "error",
                                        );
                                        return;
                                    }

                                    route("/label", true);
                                }}
                            >
                                Usuń
                            </button>
                        </span>
                    </div>
                ) : (
                    <div style="margin-top: 10px;">
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    await createLabel(label);
                                } catch (ex) {
                                    console.error(ex);
                                    showSnackbarCommand.run(
                                        "Wystąpił błąd podczas tworzenia naklejki",
                                        "error",
                                        "error",
                                    );
                                    return;
                                }

                                route("/label", true);
                            }}
                        >
                            Utwórz naklejkę
                        </button>
                    </div>
                )}
            </div>
            {label ? (
                <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
                    <h1>Rewizje produktów do których przypisana jest naklejka</h1>
                    <div>
                        <table class="list selectable">
                            <thead>
                                <tr>
                                    <th>Nazwa</th>
                                    <th>Brand</th>
                                    <th>Wersja</th>
                                    <th>Rejestrujący</th>
                                    <th>Data rejestracji</th>
                                </tr>
                            </thead>
                            <tbody>
                                {label.productTypeRevisionLabels?.map(v => (
                                    <tr
                                        class="item"
                                        onClick={() => route("/product-type-revision/" + v.productTypeRevision.id)}
                                    >
                                        <td>{v.productTypeRevision.productType!.name}</td>
                                        <td>{v.productTypeRevision.productType!.brand}</td>
                                        <td>{v.productTypeRevision.name}</td>
                                        <td>{v.productTypeRevision.creator.email}</td>
                                        <td>{parseToDateAndFormatIfExists(v.productTypeRevision.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
            {previewVisible ? <div style="height: 200px" /> : null}
        </div>
    );
}
